import { Card, Grid, Link } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import {
  ClientDTO,
  CustomFieldEnum,
  FieldTypeEnum,
  getChildClientsByParentId,
  getClientById,
} from 'wisestep-api';
import {
  ChipsType,
  Item,
  WsListingLoader,
  WsTagsList,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import { useCustomFields } from '../../../../features/clientListingPage/hooks/useCustomFields';
import { formatCustomFieldValue } from '../../../../features/clientsSettings/helper/ClientSettingsHelper';
import { failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { getCompanyInfoDetailsArray } from '../../helper/clientDetailsHelper';

interface ICompanyInfoProps {
  clientId: string;
  isChild: boolean;
  parentName: string;
}

const CompanyInfoTab = ({ clientId }: ICompanyInfoProps) => {
  const [companyInfo, setCompanyInfo] = useState<ClientDTO>();
  const { customFields } = useCustomFields(CustomFieldEnum.CLIENT);
  const visibleFields = customFields?.filter((field) => field.visible) ?? [];
  const [childClients, setChildClients] = useState<ClientDTO[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);

  useEffect(() => {
    (async () => {
      try {
        const response = await getClientById(clientId);
        setCompanyInfo(response.data.data);
        const childClientResponse = await getChildClientsByParentId(clientId, 0, 16);
        setChildClients(childClientResponse.data.data);
      } catch {
        setNotification(failed(`Failed to fetch company Information`));
      }
    })();
  }, [setNotification, clientId]);

  const detailsArray = (companyInfo && getCompanyInfoDetailsArray(companyInfo)) ?? [];

  return companyInfo && childClients ? (
    <Grid item container xs={12} spacing={1} pl={1} mt={3}>
      <Card sx={{ width: '100%' }}>
        <Grid item container xs={12} p={1}>
          <Grid item xs={12} container justifyContent="space-between">
            <Item>
              <WsTypography bold={true}>Basic Information</WsTypography>
            </Item>
          </Grid>
          <Grid item xs={1.5}>
            <Item>
              <WsTypography color={WsTypoColors.SECONDARY}>Address</WsTypography>
            </Item>
          </Grid>
          <Grid item xs={10.5}>
            <Item>
              <WsTypography>{companyInfo?.address}</WsTypography>
            </Item>
          </Grid>
          {detailsArray?.map((item, index) => (
            <Fragment key={index}>
              <Grid item xs={1.5}>
                <Item>
                  <WsTypography color={WsTypoColors.SECONDARY}>{item.label}</WsTypography>
                </Item>
              </Grid>
              <Grid item xs={2.5}>
                <Item>
                  <WsTypography>{item?.value ?? 'Not Available'}</WsTypography>
                </Item>
              </Grid>
            </Fragment>
          ))}
          <Fragment>
            <Grid item xs={1.5}>
              <Item>
                <WsTypography color={WsTypoColors.SECONDARY}>Owner</WsTypography>
              </Item>
            </Grid>
            <Grid item xs={2.5}>
              <Item>
                <Link
                  underline="hover"
                  key="1"
                  href={`/users/details-page?email=${companyInfo?.owner?.email}&isLink=true&isUserAnalytics=true`}
                >
                  <WsTypography color={WsTypoColors.PRIMARY} cursor="pointer">
                    {companyInfo?.owner?.name}
                  </WsTypography>
                </Link>
              </Item>
            </Grid>
          </Fragment>
          {visibleFields &&
            visibleFields
              .filter((field) => field.type !== FieldTypeEnum.Tags)
              .map((field) => {
                const value = companyInfo?.customFields?.[field.name ?? ''];
                const text = formatCustomFieldValue(field, value);
                return (
                  <Fragment key={field.id}>
                    <Grid item xs={1.5}>
                      <Item>
                        <WsTypography color={WsTypoColors.SECONDARY}>{field.label}</WsTypography>
                      </Item>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Item>
                        <WsTypography>{text}</WsTypography>
                      </Item>
                    </Grid>
                  </Fragment>
                );
              })}
          <Grid container item xs={12}>
            <Grid item xs={1.5}>
              <Item>
                <WsTypography color={WsTypoColors.SECONDARY}>Reason</WsTypography>
              </Item>
            </Grid>
            <Grid item xs={10.5}>
              <Item>
                {companyInfo?.clientStatusReason && (
                  <WsTypography>{`${companyInfo.clientStatusReason}`}</WsTypography>
                )}
              </Item>
            </Grid>
          </Grid>
          {companyInfo?.description && (
            <Grid container item xs={12}>
              <Grid item xs={1.5}>
                <Item>
                  <WsTypography color={WsTypoColors.SECONDARY}>Description</WsTypography>
                </Item>
              </Grid>
              <Grid item xs={10.5}>
                <Item>
                  <WsTypography>{companyInfo?.description}</WsTypography>
                </Item>
              </Grid>
            </Grid>
          )}
          {visibleFields &&
            visibleFields
              .filter((field) => field.type === FieldTypeEnum.Tags)
              .map((field) => (
                <Grid container item xs={12} key={field.name} alignItems="center">
                  <Grid item xs={1.5}>
                    <Item>
                      <WsTypography color={WsTypoColors.SECONDARY}>{field.label}</WsTypography>
                    </Item>
                  </Grid>
                  <Grid item xs={10.5}>
                    <Item>
                      <WsTagsList
                        chipArray={companyInfo.customFields?.[field.name ?? ''] ?? []}
                        count={8}
                        type={ChipsType.SECONDARY}
                      />
                    </Item>
                  </Grid>
                </Grid>
              ))}
        </Grid>
      </Card>
    </Grid>
  ) : (
    <WsListingLoader />
  );
};

export default CompanyInfoTab;
